import styled from 'styled-components';
import { brandTheme } from '../../../theme/brandTokens';
import { ModalDialog } from '../ConfirmDeleteModal/ConfirmDeleteModal.styles';

/**
 * Team & Workspaces, matching version3's routes/team.tsx.
 *
 * Radii follow version3's scale (sm 10, md 12, lg 14, xl 18, 2xl 22, full
 * 999) and font sizes Tailwind's paired line heights (xs 12/16, sm 14/20,
 * base 16/24, 2xl 24/32).
 *
 * Careful: a backtick inside a CSS comment ends the template literal early and
 * the module throws on load. Keep comments backtick-free.
 */

const FG = 'var(--hd-foreground)';
const MUTED_FG = 'var(--hd-muted-foreground)';
const MUTED = 'var(--hd-muted)';
const BORDER = 'var(--hd-border)';
const BG = 'var(--hd-background)';
const CARD = 'var(--hd-card)';
const PRIMARY = 'var(--hd-primary)';
const GRADIENT = 'var(--hd-gradient-houdiny)';
const DESTRUCTIVE = 'var(--hd-destructive)';

const MUTED_70 = 'color-mix(in srgb, var(--hd-muted) 70%, transparent)';
const PRIMARY_10 = 'color-mix(in srgb, var(--hd-primary) 10%, transparent)';
const PRIMARY_30 = 'color-mix(in srgb, var(--hd-primary) 30%, transparent)';
const FG_30 = 'color-mix(in srgb, var(--hd-foreground) 30%, transparent)';
const AMBER = '#d97706';
const AMBER_10 = 'rgba(245, 158, 11, 0.1)';

/* A select-shaped control: h-10 or h-9, rounded-lg, bordered, on the page background. */
const selectLike = height => `
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: ${height}px;
  padding: 0 12px;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  font-size: 14px;
  line-height: 20px;
  color: ${FG};
  background: ${BG};

  svg {
    color: ${MUTED_FG};
  }
`;

export const StyledTeam = styled.div`
  ${brandTheme}
  min-height: 100%;
  background: ${BG};
  color: ${FG};
  font-family: var(--base-font-sans-serif);
  -webkit-font-smoothing: antialiased;

  /* border-b, h-14, max-w-[1480px] px-8 */
  .team-subbar {
    border-bottom: 1px solid ${BORDER};
    background: ${BG};
  }

  .team-subbar-inner {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 56px;
    max-width: 1480px;
    margin: 0 auto;
    padding: 0 32px;

    @media (max-width: 767px) {
      padding: 0 16px;
    }
  }

  .team-back {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 14px;
    color: ${MUTED_FG};
    background: transparent;
    cursor: pointer;
    transition:
      background 0.15s ease,
      color 0.15s ease;

    &:hover {
      color: ${FG};
      background: ${MUTED};
    }
  }

  .team-subbar-label {
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: ${MUTED_FG};
  }

  /* mx-auto max-w-5xl px-8 py-8 */
  .team-main {
    max-width: 1024px;
    margin: 0 auto;
    padding: 32px;

    @media (max-width: 767px) {
      padding: 24px 16px;
    }
  }

  /* mb-8: text-2xl font-semibold tracking-tight */
  .team-head {
    margin-bottom: 32px;
  }

  .team-title {
    margin: 0;
    font-size: 24px;
    line-height: 32px;
    font-weight: 600;
    letter-spacing: -0.025em;
    /* GlobalStyles capitalises every heading; version3 does not. */
    text-transform: none;
    color: ${FG};
  }

  .team-section {
    margin-bottom: 40px;
  }

  .section-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;

    @media (max-width: 639px) {
      flex-direction: column;
      align-items: stretch;
    }
  }

  /* text-base font-semibold tracking-tight */
  .section-title {
    margin: 0;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    letter-spacing: -0.025em;
    text-transform: none;
    color: ${FG};
  }

  .section-lead {
    margin: 4px 0 0;
    font-size: 14px;
    line-height: 20px;
    color: ${MUTED_FG};
  }

  .section-head-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 20px;

    svg {
      color: ${MUTED_FG};
    }
  }

  /* The same primary button as Create campaign and Connect mailbox. */
  .new-ws-btn {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 36px;
    padding: 0 16px;
    border: none;
    border-radius: 18px;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    color: #fff;
    background: ${GRADIENT};
    box-shadow: var(--hd-shadow-card);
  }

  /* ---- Workspaces ---- */

  .ws-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .ws-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 16px;
    border: 1px solid ${BORDER};
    border-radius: 22px;
    font-family: inherit;
    text-align: left;
    background: ${BG};
    cursor: pointer;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;

    &:hover {
      border-color: ${FG_30};
    }

    &.is-active {
      border-color: ${PRIMARY};
      box-shadow: 0 0 0 1px ${PRIMARY_30};
      cursor: default;
    }
  }

  .ws-identity {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .ws-text {
    min-width: 0;
  }

  .ws-icon {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 40px;
    height: 40px;
    border-radius: 18px;
    color: #fff;
    background: ${GRADIENT};
  }

  .ws-name {
    overflow: hidden;
    font-size: 15px;
    line-height: 22px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${FG};
  }

  .ws-meta {
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED_FG};
  }

  .ws-active-pill {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: ${PRIMARY};
    background: ${PRIMARY_10};
  }

  .ws-switch {
    flex-shrink: 0;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: ${MUTED_FG};
    transition: color 0.15s ease;
  }

  .ws-card:hover .ws-switch {
    color: ${FG};
  }

  /* ---- Invite: rounded-2xl border bg-card p-3, row from sm ---- */

  .invite-panel {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border: 1px solid ${BORDER};
    border-radius: 22px;
    background: ${CARD};

    @media (min-width: 640px) {
      flex-direction: row;
      align-items: center;
    }
  }

  .invite-email {
    display: flex;
    flex: 1;
    align-items: center;
    gap: 8px;
    min-width: 0;
    padding: 0 12px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    background: ${BG};

    svg {
      flex-shrink: 0;
      color: ${MUTED_FG};
    }

    input {
      flex: 1;
      min-width: 0;
      height: 40px;
      border: none;
      font-family: inherit;
      font-size: 14px;
      line-height: 20px;
      color: ${FG};
      background: transparent;
      outline: none;

      &::placeholder {
        color: ${MUTED_FG};
      }

      &:disabled {
        cursor: not-allowed;
      }
    }

    &.is-invalid {
      border-color: ${DESTRUCTIVE};
    }
  }

  /* An email the invite cannot take (a "+" alias), under the invite row. */
  .invite-error {
    margin: 8px 0 0 4px;
    font-size: 12px;
    line-height: 16px;
    color: ${DESTRUCTIVE};
  }

  /* rounded-lg bg-primary px-5 py-2 text-sm font-medium */
  .invite-btn {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 40px;
    padding: 0 20px;
    border: none;
    border-radius: 14px;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: var(--hd-primary-foreground);
    background: ${PRIMARY};
  }

  /* ---- Members: overflow-hidden rounded-2xl border bg-background ---- */

  .team-list {
    overflow: visible;
    border: 1px solid ${BORDER};
    border-radius: 22px;
    background: ${BG};
  }

  /* flex items-center gap-4 px-5 py-4, border-b between rows */
  .team-row {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;

    & + .team-row {
      border-top: 1px solid ${BORDER};
    }

    @media (max-width: 767px) {
      flex-wrap: wrap;
    }
  }

  .row-avatar {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    overflow: hidden;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    font-size: 14px;
    font-weight: 600;
    color: #fff;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .row-identity {
    flex: 1;
    min-width: 0;
  }

  .row-name-line {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .row-name {
    overflow: hidden;
    font-size: 15px;
    line-height: 22px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${FG};
  }

  .row-email {
    overflow: hidden;
    font-size: 14px;
    line-height: 20px;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${MUTED_FG};
  }

  /* rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide */
  .row-pill {
    flex-shrink: 0;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    line-height: 15px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    color: ${PRIMARY};
    background: ${PRIMARY_10};

    &.pending {
      color: ${AMBER};
      background: ${AMBER_10};
    }
  }

  /* h-9 rounded-lg border bg-background px-3 text-sm */
  .row-role {
    ${selectLike(36)}
    /* Same width as the role picker, so the column lines up. */
    min-width: 132px;
  }

  /* ---- Role picker (RoleSelect.jsx) ---- */

  .role-select {
    position: relative;
    flex-shrink: 0;
    min-width: 132px;

    &.size-lg {
      min-width: 150px;
    }
  }

  .rs-trigger {
    ${selectLike(36)}
    width: 100%;
    font-family: inherit;
    cursor: pointer;
    transition:
      border-color 0.12s ease,
      box-shadow 0.12s ease;

    &:hover:not(:disabled) {
      border-color: ${FG_30};
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  .role-select.size-lg .rs-trigger {
    height: 40px;
  }

  .role-select.open .rs-trigger {
    border-color: ${PRIMARY_30};
    box-shadow: 0 0 0 3px ${PRIMARY_10};
  }

  .rs-value {
    flex: 1;
    text-align: left;
    font-weight: 500;
  }

  .rs-chevron {
    transition: transform 0.15s ease;
  }

  .role-select.open .rs-chevron {
    transform: rotate(180deg);
  }

  .rs-menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 30;
    width: 180px;
    padding: 6px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    background: ${CARD};
    box-shadow: 0 12px 32px -12px rgba(15, 23, 42, 0.25);
  }

  .rs-option {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border: none;
    border-radius: 10px;
    font-family: inherit;
    text-align: left;
    color: ${FG};
    background: transparent;
    cursor: pointer;

    &:hover {
      background: ${MUTED_70};
    }

    &.selected {
      background: ${PRIMARY_10};
    }
  }

  .rs-option-icon {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 28px;
    height: 28px;
    border-radius: 10px;
    color: ${MUTED_FG};
    background: ${MUTED};
  }

  .rs-option.selected .rs-option-icon {
    color: ${PRIMARY};
    background: ${CARD};
  }

  .rs-option-label {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
  }

  .rs-check {
    flex-shrink: 0;
    color: ${PRIMARY};
  }

  /* ---- Active workspace actions ---- */

  .ws-head-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  .ws-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 14px;
    border: 1px solid ${BORDER};
    border-radius: 18px;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: ${FG};
    background: ${BG};
    cursor: pointer;
    transition:
      background 0.15s ease,
      color 0.15s ease,
      border-color 0.15s ease;

    &:hover {
      background: ${MUTED};
    }

    &.danger:hover {
      color: #dc2626;
      border-color: rgba(220, 38, 38, 0.3);
      background: rgba(220, 38, 38, 0.06);
    }
  }

  .row-remove:not([aria-disabled='true']) {
    cursor: pointer;
    transition:
      background 0.15s ease,
      color 0.15s ease;

    &:hover {
      color: #dc2626;
      background: rgba(220, 38, 38, 0.08);
    }
  }

  /* rounded-lg bg-muted px-3 py-2 text-sm font-medium */
  .row-loginas {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 12px;
    border: none;
    border-radius: 14px;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: ${FG};
    background: ${MUTED};
    transition: background 0.15s ease;

    &:hover {
      background: ${MUTED_70};
    }
  }

  /* h-9 w-9 rounded-lg text-muted-foreground, destructive on hover */
  .row-remove {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 14px;
    color: ${MUTED_FG};
    background: transparent;
  }

  .team-empty {
    padding: 40px;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    color: ${MUTED_FG};

    &.is-card {
      border: 1px solid ${BORDER};
      border-radius: 22px;
    }
  }

  .team-pager {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;

    ul {
      margin: 0;
    }
  }
`;

/**
 * New-workspace and rename dialogs: the shared delete modal's card, close
 * button, icon tile and full-width paired buttons, plus a name field.
 */
export const WorkspaceModalDialog = styled(ModalDialog)`
  /* Wide enough for the description on one line. */
  max-width: 480px;

  && p {
    max-width: none;
  }

  .tile.brand {
    color: #fff;
    background: ${GRADIENT};
    box-shadow: var(--hd-shadow-card);
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 20px;
    text-align: left;

    span {
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
      color: ${FG};
    }
  }

  input {
    width: 100%;
    height: 44px;
    padding: 0 14px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    color: ${FG};
    background: ${BG};
    outline: none;
    transition:
      border-color 0.12s ease,
      box-shadow 0.12s ease;

    &::placeholder {
      color: ${MUTED_FG};
    }

    &:focus {
      border-color: ${PRIMARY_30};
      box-shadow: 0 0 0 3px ${PRIMARY_10};
    }
  }

  && p.note {
    max-width: none;
    margin: 8px 0 0;
    font-size: 12px;
    line-height: 16px;
    text-align: left;
  }
`;
